import React, { useEffect, useState } from "react";
import TagInput from "../../../common/TagInput";
import { fournisseurService } from "../../../../services/fournisseurService";
import styles from "./EnvoyerAuFinanceurModal.module.scss";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * LS-668 / LS-687 — modale d'envoi de l'accord de financement au fournisseur,
 * calquée sur EnvoyerAuFinanceurModal (LS-625) : destinataire, Cc et
 * commentaire à gauche (avec autosuggestion depuis le carnet d'adresses du
 * fournisseur — choix libre à chaque envoi, hors carnet toujours saisissable),
 * aperçu du mail à droite. Préremplit depuis `dernierEnvoi` (renvoi) si
 * fourni, sinon champs vides (pas d'email "principal" sur le fournisseur,
 * cf. LS-687 : le choix est toujours libre, contrairement au financeur).
 */
const EnvoyerAccordFournisseurModal = ({
  isOpen,
  lignes = [],
  expediteurNom,
  expediteurSignatureUrl,
  fournisseurUuid,
  dernierEnvoi,
  onSubmit,
  onClose,
}) => {
  const [destinataire, setDestinataire] = useState("");
  const [cc, setCc] = useState([]);
  const [commentaire, setCommentaire] = useState("");
  const [carnet, setCarnet] = useState([]);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsPreparing(true);

    if (dernierEnvoi) {
      setDestinataire(dernierEnvoi.destinataire || "");
      setCc(dernierEnvoi.cc || []);
      setCommentaire(dernierEnvoi.commentaire || "");
    } else {
      setDestinataire("");
      setCc([]);
      setCommentaire("");
    }

    if (fournisseurUuid) {
      fournisseurService
        .getFournisseur(fournisseurUuid)
        .then((res) => {
          if (!cancelled) setCarnet(res.data?.emails || []);
        })
        .catch(() => {
          if (!cancelled) setCarnet([]);
        })
        .finally(() => {
          if (!cancelled) setIsPreparing(false);
        });
    } else {
      setCarnet([]);
      setIsPreparing(false);
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, fournisseurUuid, dernierEnvoi]);

  if (!isOpen) return null;

  const trimmedDestinataire = destinataire.trim();
  const emailOk =
    trimmedDestinataire !== "" && EMAIL_REGEX.test(trimmedDestinataire);
  const emailInvalide = trimmedDestinataire !== "" && !emailOk;
  const isValid = emailOk && !!fournisseurUuid;
  const busy = isSubmitting;
  const quantiteTotale = lignes.reduce(
    (sum, l) => sum + (Number(l.quantite) || 0),
    0,
  );

  const handleCancel = () => {
    if (busy) return;
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleCancel();
  };

  const handleSubmit = async () => {
    if (!isValid || busy) return;
    setIsSubmitting(true);
    const ok = await onSubmit({
      destinataire: trimmedDestinataire,
      cc,
      commentaire: commentaire.trim() || null,
    });
    setIsSubmitting(false);
    if (ok) onClose();
  };

  const manque = [];
  if (!emailOk) manque.push("un destinataire valide");
  if (!fournisseurUuid) {
    manque.push(
      "un fournisseur identifié (régénérez l'accord si nécessaire)",
    );
  }

  return (
    <div
      className={`modal d-block ${styles.backdrop}`}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="envoyer-fournisseur-title"
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`modal-content ${styles.content}`}>
          <div className={styles.header}>
            <div className={styles.headerIcon} aria-hidden="true">
              <i className="fa fa-paper-plane" />
            </div>
            <div className={styles.headerText}>
              <h5 className="modal-title" id="envoyer-fournisseur-title">
                {dernierEnvoi ? "Renvoyer" : "Envoyer"} l'accord au fournisseur
              </h5>
              <small>
                L'accord de financement généré sera joint au mail.
              </small>
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleCancel}
              aria-label="Fermer"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className={`${styles.body} ${styles.bodyPreviewWide}`}>
            {isPreparing ? (
              <div className={styles.loading}>
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                Préparation…
              </div>
            ) : (
              <>
                <div className={styles.fieldsCol}>
                  <div
                    className={`${styles.field} ${
                      emailInvalide ? styles.fieldError : ""
                    }`}
                  >
                    <label htmlFor="envoyer-fournisseur-destinataire">
                      Destinataire
                    </label>
                    <input
                      id="envoyer-fournisseur-destinataire"
                      type="email"
                      className="form-control"
                      value={destinataire}
                      onChange={(e) => setDestinataire(e.target.value)}
                      placeholder="contact@fournisseur.fr"
                      disabled={busy}
                      autoFocus
                      list={
                        carnet.length > 0
                          ? "envoyer-fournisseur-destinataire-suggestions"
                          : undefined
                      }
                    />
                    {carnet.length > 0 && (
                      <datalist id="envoyer-fournisseur-destinataire-suggestions">
                        {carnet.map((email) => (
                          <option key={email} value={email} />
                        ))}
                      </datalist>
                    )}
                    {emailInvalide && (
                      <p className={styles.errorText}>
                        Cette adresse email n'est pas valide.
                      </p>
                    )}
                  </div>

                  <TagInput
                    name="cc"
                    label="Copie (Cc)"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="Ajouter un email en copie..."
                    suggestions={carnet}
                    className={styles.ccField}
                  />
                </div>

                <div className={styles.previewCol}>
                  <p className={styles.colLabel}>Aperçu du mail</p>
                  <div className={styles.mailCard}>
                    <div className={styles.mailAttachRow}>
                      <span className={styles.attachChip}>
                        <i className="fa fa-paperclip" aria-hidden="true" />
                        Accord de financement (PDF)
                      </span>
                      <p className={styles.attachNote}>
                        Le PDF généré sera automatiquement joint au mail.
                      </p>
                    </div>
                    <div className={styles.mailBody}>
                      <p>Bonjour,</p>
                      <p>
                        Veuillez trouver ci-joint l'accord de financement pour
                        la demande concernée, portant sur {quantiteTotale}{" "}
                        véhicule
                        {quantiteTotale > 1 ? "s" : ""} :
                      </p>
                      <table className="table table-sm table-bordered mb-2">
                        <thead>
                          <tr>
                            <th>Véhicule</th>
                            <th>Qté</th>
                            <th>HT unit.</th>
                            <th>TTC unit.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.length === 0 ? (
                            <tr>
                              <td colSpan={4}>
                                Aucun devis rattaché à la demande.
                              </td>
                            </tr>
                          ) : (
                            lignes.map((l, i) => (
                              <tr key={i}>
                                <td>{l.vehicule || "—"}</td>
                                <td>{l.quantite}</td>
                                <td className={styles.amountCell}>
                                  {Number(
                                    l.montantHtUnitaire || 0,
                                  ).toLocaleString("fr-FR")}{" "}
                                  €
                                </td>
                                <td className={styles.amountCell}>
                                  {Number(
                                    l.montantTtcUnitaire || 0,
                                  ).toLocaleString("fr-FR")}{" "}
                                  €
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      <textarea
                        className={styles.commentInput}
                        rows={2}
                        value={commentaire}
                        onChange={(e) => setCommentaire(e.target.value)}
                        disabled={busy}
                        placeholder="Ajouter un commentaire dans le mail (optionnel)…"
                        aria-label="Commentaire ajouté au mail"
                      />
                      <p>En vous remerciant par avance.</p>
                      <p>Vous pouvez répondre directement à ce message.</p>
                      <p>Bien cordialement</p>
                      <p style={{ marginBottom: 0 }}>
                        Pour Leasecar, par {expediteurNom || "—"}
                      </p>
                      {expediteurSignatureUrl && (
                        <p style={{ marginTop: 4, marginBottom: 0 }}>
                          <img
                            src={expediteurSignatureUrl}
                            alt="signature"
                            style={{ maxHeight: 60 }}
                          />
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.footer}>
            <p className={styles.footerHint}>
              {!isPreparing && manque.length > 0
                ? `Pour envoyer, renseignez ${manque.join(" et ")}.`
                : ""}
            </p>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={handleCancel}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.btnSend}
              onClick={handleSubmit}
              disabled={busy || !isValid || isPreparing}
            >
              <i
                className={`fa fa-${busy ? "spinner fa-spin" : "paper-plane"}`}
                aria-hidden="true"
              />
              {busy ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvoyerAccordFournisseurModal;
